import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import { setVariableDataBindingAction } from 'editor/src/store/design/slice';
import { MediaImage, MediaText, VariableDataBinding } from 'editor/src/store/design/types';
import updateTextPropertiesWithoutRender from 'editor/src/store/design/util/updateTextPropertiesWithoutRender';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import type { RootState } from 'editor/src/store/index';

import { getVariableDataColumnValue } from 'editor/src/lib/variableData';

import dissolveLoneSharedTextFieldOperation from './dissolveLoneSharedTextFieldOperation';
import updateMediaElementOperation, { MediaUpdateActionName } from './updateMediaElementOperation';

/**
 * Binds the design to a code set, or clears the binding.
 *
 * Bound elements follow the set: a text whose column survives is re-poured
 * with the new sample value so the canvas keeps showing something realistic,
 * and a text or QR field naming a column the new set lacks is unbound rather
 * than left pointing at a value that will never exist. Clearing the binding
 * unbinds everything for the same reason. A QR that would lose its last field
 * keeps it instead (see below). One undo step for the whole move.
 */
const setVariableDataBindingOperation =
  (binding: VariableDataBinding | undefined) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const { designData } = getState().design;
    if (!designData) {
      return;
    }
    const columns = new Set(binding?.columns ?? []);

    batch(() => {
      dispatch(saveUndoRedoStateOperation(MediaUpdateActionName.UNKNOWN));
      dispatch(setVariableDataBindingAction(binding));

      designData.spreads.forEach((spread, spreadIndex) =>
        spread.pages.forEach((page, pageIndex) =>
          page.groups.media?.forEach((_, elementIndex) => {
            const address = { spreadIndex, pageIndex, elementIndex };
            // Read back rather than from the snapshot: dissolving a group rewrites a
            // partner this loop may not have reached yet.
            const element = getMediaElement(getState(), address);
            if (!element) {
              return;
            }

            if (element.type === 'text') {
              const text = element as MediaText;
              const column = text.extra.variableColumn;
              if (!column) {
                return;
              }
              const keep = binding && columns.has(column);
              const sample = keep ? getVariableDataColumnValue(binding, column) : undefined;
              // Binding takes the layer out of its shared group, which is a
              // departure like any other: a survivor left alone keeps an id that
              // shares nothing and dead ends the panel.
              if (keep) {
                dispatch(dissolveLoneSharedTextFieldOperation(text));
              }
              const textUpdate: Partial<MediaText> = {
                extra: {
                  ...text.extra,
                  variableColumn: keep ? column : undefined,
                  // Unbinding clears the value rather than keeping it: what stands
                  // there came from the old set, and left behind it becomes the
                  // default of an ordinary buyer field — a real-looking code that
                  // prints when nobody types over it.
                  text: keep ? sample ?? text.extra.text : '',
                  sharedFieldId: keep ? undefined : text.extra.sharedFieldId,
                },
              };
              // The new string has its own width, and horizontal reflow packs by it.
              updateTextPropertiesWithoutRender(text, textUpdate, undefined);
              dispatch(updateMediaElementOperation(address, textUpdate, MediaUpdateActionName.TEXT_UPDATED, true));
              return;
            }

            if (element.type === 'image') {
              const image = element as MediaImage;
              const fields = image.qrCode?.fields;
              if (!fields?.some((field) => field.source.type === 'column')) {
                return;
              }
              const kept = fields.filter(
                (field) => field.source.type !== 'column' || (binding && columns.has(field.source.column)),
              );
              // Nothing redraws a QR with no fields, so its placed image, the old sample's
              // code, would print on every unit. Left naming the missing column, it shows
              // as missing to the seller and holds the buyer back until it is remapped.
              const nextFields = kept.length > 0 ? kept : fields;
              dispatch(
                updateMediaElementOperation(
                  address,
                  { qrCode: { ...image.qrCode!, fields: nextFields, encoded: undefined } },
                  MediaUpdateActionName.UNKNOWN,
                  true,
                ),
              );
            }
          }),
        ),
      );
    });
  };

export default setVariableDataBindingOperation;
